import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row, Card, CardBody, CardTitle, Spinner, Alert, Table, Input, Label } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { apiJson } from "../../../helpers/apiConfig";

/**
 * Oturum Raporu — "sisteme giren / çıkan" sayıları (reklamverenlere gösterilecek
 * aktif kullanıcı verisi).
 *
 * Veri: api/Oturum/Rapor (admin) → OTURUM_LOGLARI. Mobil uygulama ön plana gelince
 * GIRIS, arka plana gidince CIKIS (+süre) yazar. Cihaz bazlı sayılır; misafirler
 * dahildir. Sayılar ancak API + bu özelliği içeren mobil sürüm yayına alındıktan
 * sonra birikir — eski sürümler göndermez.
 */

const PLATFORM_AD = { ios: "iOS", android: "Android", web: "Web", "?": "Bilinmiyor" };
const sayi = (v) => Number(v || 0).toLocaleString("tr-TR");
const sure = (sn) => {
  if (sn == null) return "—";
  const d = Math.floor(sn / 60);
  const s = sn % 60;
  return d > 0 ? `${d} dk ${s} sn` : `${s} sn`;
};
const gunAd = (g) =>
  new Date(g).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", weekday: "short" });

const Kutu = ({ baslik, deger, alt, ikon }) => (
  <Col xl={true} md={4} sm={6} className="mb-3">
    <Card className="mini-stats-wid h-100">
      <CardBody>
        <div className="d-flex">
          <div className="flex-grow-1">
            <p className="text-muted fw-medium mb-2">{baslik}</p>
            <h4 className="mb-0">{deger}</h4>
            {alt ? <p className="text-muted mb-0 mt-2 small">{alt}</p> : null}
          </div>
          <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
            <span className="avatar-title rounded-circle bg-primary">
              <i className={`${ikon} font-size-24`}></i>
            </span>
          </div>
        </div>
      </CardBody>
    </Card>
  </Col>
);

const OturumRaporu = () => {
  document.title = "Oturum Raporu | HarmanaGel Admin";

  const [gun, setGun] = useState(30);
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    setHata(null);
    try {
      const v = await apiJson(`/api/Oturum/Rapor?gun=${gun}`);
      if (v?.tabloYok) {
        setHata("OTURUM_LOGLARI tablosu bulunamadı — OTURUM_LOGLARI.sql çalıştırılmalı.");
        setVeri(null);
      } else {
        setVeri(v);
      }
    } catch (e) {
      setHata(
        e.status === 403
          ? "Bu rapor yalnızca yöneticilere açık."
          : `Rapor alınamadı: ${e.message}. API güncellendi mi?`
      );
    } finally {
      setYukleniyor(false);
    }
  }, [gun]);

  useEffect(() => {
    yukle();
  }, [yukle]);

  const ozet = veri?.ozet || {};
  const gunluk = veri?.gunluk || [];
  const gunSayisi = gunluk.length || 1;
  const ortGunlukTekil = Math.round(gunluk.reduce((t, g) => t + (g.TEKIL_CIHAZ || 0), 0) / gunSayisi);

  // Saatlik yoğunluk: 0-23 tüm saatler (boş saat 0).
  const saatGrafik = useMemo(() => {
    const m = {};
    (veri?.saatlik || []).forEach((s) => (m[s.SAAT] = s.GIRIS));
    const saatler = Array.from({ length: 24 }, (_, i) => i);
    return {
      seri: [{ name: "Giriş", data: saatler.map((h) => m[h] || 0) }],
      opsiyon: {
        chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
        plotOptions: { bar: { columnWidth: "60%", borderRadius: 4, borderRadiusApplication: "end" } },
        dataLabels: { enabled: false },
        colors: ["#556ee6"],
        stroke: { show: true, width: 2, colors: ["transparent"] },
        grid: { borderColor: "#f1f1f1", strokeDashArray: 3 },
        xaxis: {
          categories: saatler.map((h) => `${String(h).padStart(2, "0")}:00`),
          labels: { rotate: -45, style: { fontSize: "10px" } },
        },
        yaxis: { labels: { formatter: (v) => sayi(Math.round(v)) } },
        tooltip: { y: { formatter: (v) => `${sayi(v)} giriş` } },
      },
    };
  }, [veri]);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Yönetim" breadcrumbItem="Oturum Raporu" />

          <Row className="mb-3 align-items-end">
            <Col xs="auto">
              <Label for="oturum-gun" className="mb-1">Dönem</Label>
              <Input id="oturum-gun" type="select" value={gun} onChange={(e) => setGun(Number(e.target.value))}>
                <option value={1}>Bugün</option>
                <option value={7}>Son 7 gün</option>
                <option value={30}>Son 30 gün</option>
                <option value={90}>Son 90 gün</option>
                <option value={365}>Son 1 yıl</option>
              </Input>
            </Col>
            <Col>{yukleniyor ? <Spinner size="sm" /> : null}</Col>
          </Row>

          {hata ? <Alert color="danger">{hata}</Alert> : null}

          <Row>
            <Kutu baslik="Şu an uygulamada" deger={sayi(veri?.simdiIceride)} alt="Son 30 dk'da giriş yapıp çıkmamış cihaz" ikon="bx bx-pulse" />
            <Kutu baslik="Tekil cihaz (dönem)" deger={sayi(ozet.TEKIL_CIHAZ)} alt={`Günlük ort. ${sayi(ortGunlukTekil)} cihaz`} ikon="bx bx-mobile-alt" />
            <Kutu baslik="Giriş yapan üye" deger={sayi(ozet.TEKIL_UYE)} alt="Hesabıyla açan tekil kullanıcı" ikon="bx bx-user-check" />
            <Kutu baslik="Giriş / çıkış" deger={`${sayi(ozet.GIRIS)} / ${sayi(ozet.CIKIS)}`} alt="Uygulamayı açma / kapatma sayısı" ikon="bx bx-log-in-circle" />
            <Kutu baslik="Ortalama oturum" deger={sure(ozet.ORT_SURE_SN)} alt="Açılıştan arka plana kadar" ikon="bx bx-time-five" />
          </Row>

          <Row>
            <Col lg={8} className="mb-3">
              <Card className="h-100">
                <CardBody>
                  <CardTitle className="mb-3">Saatlik yoğunluk (Türkiye saati, giriş sayısı)</CardTitle>
                  <ReactApexChart options={saatGrafik.opsiyon} series={saatGrafik.seri} type="bar" height={280} />
                </CardBody>
              </Card>
            </Col>
            <Col lg={4} className="mb-3">
              <Card className="h-100">
                <CardBody>
                  <CardTitle className="mb-3">Platform</CardTitle>
                  {veri?.platformlar?.length ? (
                    <Table className="table-sm align-middle mb-0">
                      <thead>
                        <tr><th>Platform</th><th className="text-end">Tekil cihaz</th><th className="text-end">Giriş</th></tr>
                      </thead>
                      <tbody>
                        {veri.platformlar.map((p) => (
                          <tr key={p.PLATFORM}>
                            <td>{PLATFORM_AD[p.PLATFORM] || p.PLATFORM}</td>
                            <td className="text-end">{sayi(p.TEKIL_CIHAZ)}</td>
                            <td className="text-end">{sayi(p.GIRIS)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <p className="text-muted mb-0">Bu dönemde kayıt yok.</p>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Card>
            <CardBody>
              <CardTitle className="mb-3">Günlük döküm</CardTitle>
              {gunluk.length ? (
                <div className="table-responsive">
                  <Table className="table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Gün</th>
                        <th className="text-end">Tekil cihaz</th>
                        <th className="text-end">Giriş yapan üye</th>
                        <th className="text-end">Giriş</th>
                        <th className="text-end">Çıkış</th>
                        <th className="text-end">Ort. oturum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...gunluk].reverse().map((g) => (
                        <tr key={g.GUN}>
                          <td>{gunAd(g.GUN)}</td>
                          <td className="text-end fw-semibold">{sayi(g.TEKIL_CIHAZ)}</td>
                          <td className="text-end">{sayi(g.TEKIL_UYE)}</td>
                          <td className="text-end">{sayi(g.GIRIS)}</td>
                          <td className="text-end">{sayi(g.CIKIS)}</td>
                          <td className="text-end">{sure(g.ORT_SURE_SN)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <p className="text-muted mb-0">Bu dönemde kayıt yok.</p>
              )}
            </CardBody>
          </Card>

          <p className="text-muted small">
            Aynı cihazdan 30 saniye içindeki tekrar giriş/çıkışlar tek sayılır; 4 saatten uzun oturumlar 4 saat kabul edilir.
            Bildirim çekmecesi gibi kısa kesintiler çıkış sayılmaz.
          </p>
        </div>
      </div>
    </React.Fragment>
  );
};

export default OturumRaporu;
